import { X, Download, Share } from "lucide-react";
import { useTranslation } from "../i18n";
import jliLogo from "../images/jli-logo-dark.svg";

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

interface PWAInstallPromptProps {
    show: boolean;
    onDismiss: () => void;
    deferredPromptRef: React.MutableRefObject<any>;
}

const PWAInstallPrompt = ({ show, onDismiss, deferredPromptRef }: PWAInstallPromptProps) => {
    const { t } = useTranslation();

    const isStandalone =
        (window.matchMedia?.("(display-mode: standalone)")?.matches ?? false) ||
        (window.navigator as any).standalone === true;

    const handleInstall = async () => {
        const prompt = deferredPromptRef.current;
        if (!prompt) return;

        prompt.prompt();
        const { outcome } = await prompt.userChoice;

        if (outcome === "accepted") {
            deferredPromptRef.current = null;
        }
        onDismiss();
    };

    if (isStandalone || !show) return null;

    // iOS Safari: manual instructions
    if (isIOS && !deferredPromptRef.current) {
        return (
            <div className="pwa-prompt-container">
                <div className="pwa-prompt-card">
                    <div className="pwa-prompt-row" style={{ marginBottom: 12 }}>
                        <img src={jliLogo} alt="JLI" className="pwa-prompt-icon" />
                        <div className="pwa-prompt-text">
                            <p className="pwa-prompt-title">{t("pwa_ios_install_title")}</p>
                        </div>
                        <button className="pwa-prompt-dismiss" onClick={onDismiss} aria-label="Dismiss">
                            <X size={20} />
                        </button>
                    </div>
                    <div className="pwa-prompt-steps">
                        <p className="pwa-prompt-step">
                            <Share size={16} color="#FF7315" />
                            {t("pwa_ios_install_step1")}
                        </p>
                        <p className="pwa-prompt-step">
                            <Download size={16} color="#FF7315" />
                            {t("pwa_ios_install_step2")}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Android/Chrome: automated install
    if (!deferredPromptRef.current) return null;

    return (
        <div className="pwa-prompt-container">
            <div className="pwa-prompt-card">
                <div className="pwa-prompt-row">
                    <img src={jliLogo} alt="JLI" className="pwa-prompt-icon" />
                    <div className="pwa-prompt-text">
                        <p className="pwa-prompt-title">{t("pwa_install_title")}</p>
                        <p className="pwa-prompt-desc">{t("pwa_install_description")}</p>
                    </div>
                    <button className="pwa-prompt-install-btn" onClick={handleInstall}>
                        <Download size={16} />
                        {t("pwa_install_button")}
                    </button>
                    <button className="pwa-prompt-dismiss" onClick={onDismiss} aria-label="Dismiss">
                        <X size={20} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PWAInstallPrompt;
